import {
  SupplierNotificationSettingType,
  UpsertWorkOrderNotificationSettingInput,
  UpdateWorkOrderInput,
} from "@/gql/graphql";
import { useField, useFormikContext } from "formik";
import { useState, KeyboardEvent } from "react";
import { Field, Label } from "@/components/catalyst/fieldset";
import { Input } from "@headlessui/react";
import { XMarkIcon } from "@heroicons/react/20/solid";

export function useNotificationSettingsFields() {
  const { setFieldValue } = useFormikContext<UpdateWorkOrderInput>();
  const [field] = useField<UpsertWorkOrderNotificationSettingInput[]>(
    "notificationSettings.upsert",
  );
  const [deleteField] = useField<string[]>("notificationSettings.delete");

  const toEmails =
    field.value?.filter(
      (entry) => entry.type === SupplierNotificationSettingType.To,
    ) ?? [];
  const ccEmails =
    field.value?.filter(
      (entry) => entry.type === SupplierNotificationSettingType.Cc,
    ) ?? [];
  const bccEmails =
    field.value?.filter(
      (entry) => entry.type === SupplierNotificationSettingType.Bcc,
    ) ?? [];

  function addEmail(
    email: string,
    type: SupplierNotificationSettingType,
  ): boolean {
    const trimmed = email.trim();
    if (!trimmed) return false;

    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(trimmed)) return false;

    const existingEmails = field.value || [];
    const isDuplicate = existingEmails.some((e) => e.email === trimmed);
    if (isDuplicate) return false;

    const newEntry: UpsertWorkOrderNotificationSettingInput = {
      email: trimmed,
      type,
    };

    setFieldValue("notificationSettings.upsert", [...existingEmails, newEntry]);
    return true;
  }

  function removeEmail(emailToRemove: string) {
    const existingEmails = field.value || [];
    const emailEntry = existingEmails.find((e) => e.email === emailToRemove);

    const updated = existingEmails.filter((e) => e.email !== emailToRemove);
    setFieldValue("notificationSettings.upsert", updated);

    if (emailEntry?.id) {
      const deleteList = deleteField.value || [];
      setFieldValue("notificationSettings.delete", [
        ...deleteList,
        emailEntry.id,
      ]);
    }
  }

  return { toEmails, ccEmails, bccEmails, addEmail, removeEmail };
}

export function WorkOrderNotificationSettingForm() {
  const { toEmails, ccEmails, bccEmails, addEmail, removeEmail } =
    useNotificationSettingsFields();

  return (
    <div className="col-span-full space-y-4">
      <EmailField
        label="To"
        type={SupplierNotificationSettingType.To}
        emails={toEmails}
        onAdd={addEmail}
        onRemove={removeEmail}
        autoFocus
      />
      <EmailField
        label="Cc"
        type={SupplierNotificationSettingType.Cc}
        emails={ccEmails}
        onAdd={addEmail}
        onRemove={removeEmail}
      />
      <EmailField
        label="Bcc"
        type={SupplierNotificationSettingType.Bcc}
        emails={bccEmails}
        onAdd={addEmail}
        onRemove={removeEmail}
      />
    </div>
  );
}

export type EmailFieldProps = {
  label: string;
  type: SupplierNotificationSettingType;
  emails: UpsertWorkOrderNotificationSettingInput[];
  onAdd: (email: string, type: SupplierNotificationSettingType) => boolean;
  onRemove: (email: string) => void;
  autoFocus?: boolean;
};

export function EmailField({
  label,
  type,
  emails,
  onAdd,
  onRemove,
  autoFocus,
}: EmailFieldProps) {
  const [inputValue, setInputValue] = useState("");
  const [error, setError] = useState<string | null>(null);

  function handleAdd() {
    const success = onAdd(inputValue, type);
    if (success) {
      setInputValue("");
      setError(null);
    } else if (inputValue.trim()) {
      setError("Invalid or duplicate email");
    }
  }

  function handleKeyDown(e: KeyboardEvent<HTMLInputElement>) {
    if (e.key === "Enter" || e.key === "Tab" || e.key === ",") {
      e.preventDefault();
      handleAdd();
    } else if (e.key === "Backspace" && !inputValue && emails.length > 0) {
      onRemove(emails[emails.length - 1].email);
    } else if (error) {
      setError(null);
    }
  }

  function handleBlur() {
    if (inputValue.trim()) {
      handleAdd();
    }
  }

  return (
    <Field>
      <Label>{label}</Label>
      <div
        className={`relative flex min-h-[calc(2.5rem+2px)] w-full flex-wrap items-center gap-2 rounded-lg border px-3 py-2 sm:min-h-[calc(2rem+2px)] ${
          error
            ? "border-red-500 hover:border-red-500"
            : "border-zinc-950/10 hover:border-zinc-950/20 dark:border-white/10 dark:hover:border-white/20"
        } focus-within:outline-hidden bg-white focus-within:ring-2 focus-within:ring-blue-500 dark:bg-white/5`}
      >
        {emails.map((entry) => (
          <span
            key={entry.email}
            className="inline-flex items-center gap-1 rounded-md bg-blue-50 px-2 py-0.5 text-sm font-medium text-blue-700 dark:bg-blue-500/10 dark:text-blue-400"
          >
            {entry.email}
            <button
              type="button"
              onClick={() => onRemove(entry.email)}
              className="rounded-sm hover:bg-blue-100 dark:hover:bg-blue-500/20"
            >
              <XMarkIcon className="h-4 w-4" />
            </button>
          </span>
        ))}

        <Input
          type="text"
          value={inputValue}
          onChange={(e) => setInputValue(e.target.value)}
          onKeyDown={handleKeyDown}
          onBlur={handleBlur}
          placeholder={
            emails.length === 0 ? "Enter email addresses" : undefined
          }
          autoFocus={autoFocus}
          className="min-w-[120px] flex-1 border-none bg-transparent p-0 text-base/6 text-zinc-950 outline-none placeholder:text-zinc-500 sm:text-sm/6 dark:text-white"
        />
      </div>
      {error ? <p className="mt-1 text-xs text-red-600">{error}</p> : null}
    </Field>
  );
}
